/**
 * Centralized configuration management for the frontend application
 * All magic numbers, timeouts, retry counts, and other configurable values should be defined here
 */

// Environment variable validation
export const getEnvVar = (key: string, defaultValue?: string): string => {
  const value = import.meta.env[key] || defaultValue
  if (!value && !defaultValue) {
    console.warn(`Environment variable ${key} is not set and no default provided`)
  }
  
  return value || ''
}

// Configuration interfaces
interface ApiConfig {
  baseUrl: string
  timeout: number
  retryConfig: {
    maxRetries: number
    retryDelay: number
    healthCheckMaxRetries: number
    healthCheckRetryDelay: number
  }
  endpoints: {
    health: string
    optimize: string
    study: string
    studies: string
    results: string
    authStatus: string
  }
}

interface QueryConfig {
  staleTime: number
  gcTime: number
  refetchInterval: number
  defaultPageSize: number
}

interface FileUploadConfig {
  maxFileSize: number // in bytes
  acceptedFormats: {
    mimeTypes: Record<string, string[]>
    extensions: string[]
  }
}

interface LoggerConfig {
  enabled: boolean
  level: 'debug' | 'info' | 'warn' | 'error'
  grouping: boolean
  requestIdLength: number
  maxLogsInPanel: number
}

interface UIConfig {
  animations: {
    duration: number
    delay: number
  }
  debounceDelay: number
  toastDuration: number
  timestampUpdateInterval: number
}

interface ErrorHandlingConfig {
  circuitBreaker: {
    default: {
      threshold: number
      timeout: number
      resetTimeout: number
    }
    studies: {
      threshold: number
      timeout: number
      resetTimeout: number
    }
    health: {
      threshold: number
      timeout: number
      resetTimeout: number
    }
  }
  retry: {
    maxAttempts: number
    initialDelay: number
    maxDelay: number
    backoffMultiplier: number
  }
  monitoring: {
    enabled: boolean
    errorWindowMs: number
    maxErrorsPerType: number
  }
}

interface UserLimitsConfig {
  defaults: {
    optimizationsToday: number
    trialsToday: number
    concurrentStudies: number
  }
}

// Metric threshold configuration for dashboard color coding
// Simplified 3-tier system: green (top 25%), yellow (middle 50%), red (bottom 25%)
// Thresholds derived from production database analysis of 2,326 trials (Dec 2025)
export interface MetricThreshold {
  green: number   // >= this value = green (top performers)
  yellow: number  // >= this value (but < green) = yellow, < this = red
}

export interface MetricThresholdsConfig {
  // Sharpe ratio: higher is better
  // Industry standard: < 1 = poor, 1-2 = adequate, > 2 = very good
  sharpe: MetricThreshold

  // Sortino ratio: higher is better
  // P75 = 1.48, P25 = 0.50 from production data
  sortino: MetricThreshold

  // Win rate: higher is better (stored as percentage 0-100)
  // P75 = 57.6%, P25 = 44.4% from production data
  winRate: MetricThreshold

  // Drawdown: LOWER is better (inverted logic)
  // P25 = 0.60%, P75 = 1.18% from production data
  drawdown: MetricThreshold

  // Trades: measures statistical confidence
  // P75 = 42, P25 = 19 from production data
  trades: MetricThreshold

  // Profit factor: higher is better, 1.0 = breakeven
  // P75 = 3.56, P25 = 1.86 from production data
  profitFactor: MetricThreshold

  // Objective score: optimization quality metric
  // P75 = 0.138, P25 = 0.033 from production data
  objectiveScore: MetricThreshold
}

interface OptimizationConfig {
  defaultParameters: {
    lookback_window: {
      min: number
      max: number
      default: number
    }
    entry_threshold: {
      min: number
      max: number
      default: number
      step: number
    }
    stop_loss_percent: {
      min: number
      max: number
      default: number
      step: number
    }
    take_profit_percent: {
      min: number
      max: number
      default: number
      step: number
    }
  }
  trialLimits: {
    min: number
    max: number
    default: number
  }
}

const normalizeUrl = (value: string): string => {
  if (!value) return value;
  if (value === "/") return value;
  return value.replace(/\/$/, "");
};

export const resolveApiBaseUrl = (): string => {
  const explicitUrl = normalizeUrl(
    getEnvVar("VITE_API_URL", "") || getEnvVar("VITE_API_BASE_URL", "") || "",
  );

  const browserOrigin =
    typeof window !== "undefined" ? window.location.origin : undefined;

  if (explicitUrl) {
    // If the explicit URL is absolute, decide whether to override based on host
    try {
      const parsed = new URL(explicitUrl, browserOrigin || "http://localhost");
      const currentHost = browserOrigin ? new URL(browserOrigin).host : undefined;

      // When running on optimizer.ai-edge.io we prefer the Vercel rewrite (/api)
      if (
        browserOrigin &&
        currentHost === "optimizer.ai-edge.io" &&
        parsed.host === "optimizer-api.ai-edge.io"
      ) {
        return "/api";
      }

      // If the explicit URL points back to the same host, use its pathname
      if (browserOrigin && currentHost === parsed.host) {
        const pathname = normalizeUrl(parsed.pathname);
        // If pathname is empty or just slash, return empty string to avoid double slashes
        return pathname === "/" ? "" : pathname || "";
      }

      // For different hosts, return the full origin (without trailing path)
      return parsed.origin;
    } catch {
      // Not a valid absolute URL; fall back to the provided string (relative path)
      return explicitUrl;
    }
  }

  if (browserOrigin) {
    const { host } = new URL(browserOrigin);
    if (host === "optimizer.ai-edge.io") {
      return "/api";
    }
  }

  // Local development default matches Vite proxy configuration
  return "/api";
};

/**
 * Resolve the API base URL for large-body, data-bearing endpoints (e.g. POST /optimize).
 *
 * The production frontend (optimizer.ai-edge.io) normally reaches the API through the
 * Vercel `/api` rewrite (see resolveApiBaseUrl). That rewrite proxies the request through
 * Vercel's edge network, which enforces a hard ~4.5MB request-body limit and returns
 * 413 FUNCTION_PAYLOAD_TOO_LARGE for anything larger. Study-start requests embed the full
 * OHLCV dataset inline and routinely exceed that limit (~30k+ candles).
 *
 * To keep these calls off Vercel's edge proxy, we post directly to the origin API
 * (VITE_API_URL, e.g. https://optimizer-api.ai-edge.io), which is CORS-enabled for the
 * frontend origin (Authorization is a bearer token, not a proxy-scoped cookie). When no
 * explicit API URL is configured (local dev), we fall back to the normal proxy base so the
 * Vite dev proxy keeps working.
 */
export const resolveDirectApiBaseUrl = (): string => {
  const explicitUrl = normalizeUrl(
    getEnvVar("VITE_API_URL", "") || getEnvVar("VITE_API_BASE_URL", "") || "",
  );

  if (explicitUrl) {
    const browserOrigin =
      typeof window !== "undefined" ? window.location.origin : undefined;

    try {
      const parsed = new URL(explicitUrl, browserOrigin || "http://localhost");

      // A *relative* VITE_API_URL (the repo ships "/api" in Dockerfile.frontend and
      // docker-compose.yml) resolves against the browser origin, so `parsed.origin`
      // would be the FRONTEND origin and the "/api" prefix would be silently dropped —
      // posting to <frontend-origin>/optimize, which nginx serves as the SPA rather
      // than proxying to FastAPI. Study start would break outright.
      //
      // Same-origin means there is no distinct direct origin to bypass the edge proxy
      // with, so defer to the normal proxy base exactly as the no-explicit-URL case
      // does. Direct posting is an optimisation, and it simply does not apply here.
      if (browserOrigin && parsed.origin === browserOrigin) {
        return resolveApiBaseUrl();
      }

      // Cross-origin API (e.g. https://optimizer-api.ai-edge.io): post straight to it,
      // which is the whole point — it keeps large bodies off Vercel's 4.5 MB edge limit.
      return parsed.origin;
    } catch {
      // Unparseable even against an origin. Fall back to the proxy base rather than
      // returning the raw string, so a malformed value degrades to a working path
      // instead of a silently wrong one.
      return resolveApiBaseUrl();
    }
  }

  // No explicit API URL (local dev / same-origin): fall back to the standard base.
  return resolveApiBaseUrl();
};

/**
 * Approximate serialized-JSON size of one OHLCV candle, in bytes. Measured ~146 bytes on
 * real full-precision data; 150 is a safe round estimate used ONLY for pre-flight
 * payload-size warnings — never for correctness or for altering the user's data.
 */
export const ESTIMATED_BYTES_PER_CANDLE = 150;

/**
 * Maximum study-start request payload the UI will accept, in bytes. Deliberately set ~2 MB
 * BELOW the backend MAX_OPTIMIZE_REQUEST_BYTES (30 MiB) so the honest pre-flight warning
 * always fires before the server's hard 413 — the candle-count estimate excludes the request
 * envelope (study_name/toml_config/parameters), and this margin absorbs it. Also drives the
 * upload widget's file-size cap so the advertised limit matches reality.
 */
export const MAX_REQUEST_PAYLOAD_BYTES = 28 * 1024 * 1024;

// Main configuration object
export const config = {
  // API Configuration
  api: {
    baseUrl: resolveApiBaseUrl(),
    timeout: 10000, // 10 seconds
    retryConfig: {
      maxRetries: 3,
      retryDelay: 1000, // 1 second
      healthCheckMaxRetries: 5,
      healthCheckRetryDelay: 500, // 0.5 seconds
    },
    endpoints: {
      health: '/health',
      optimize: '/optimize',
      study: '/study',
      studies: '/studies',
      results: '/results',
      authStatus: '/auth/status',
    },
  } as ApiConfig,

  // React Query Configuration
  query: {
    staleTime: 10000, // Consider data fresh for 10 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchInterval: 5000, // 5 seconds (now only used as fallback when real-time fails)
    defaultPageSize: 100,
  } as QueryConfig,

  // File Upload Configuration
  fileUpload: {
    // Match the study-start payload cap so the advertised "Max" limit reflects what can
    // actually be sent (the parsed dataset becomes the request body). See MAX_REQUEST_PAYLOAD_BYTES.
    maxFileSize: MAX_REQUEST_PAYLOAD_BYTES,
    acceptedFormats: {
      mimeTypes: {
        'application/json': ['.json'],
        'text/csv': ['.csv'],
      },
      extensions: ['.json', '.csv'],
    },
  } as FileUploadConfig,

  // Logger Configuration
  logger: {
    enabled: import.meta.env.DEV || getEnvVar('VITE_ENABLE_LOGGING', 'false') === 'true',
    level: (getEnvVar('VITE_LOG_LEVEL', import.meta.env.PROD ? 'error' : 'info') as LoggerConfig['level']),
    grouping: true,
    requestIdLength: 8,
    maxLogsInPanel: 1000,
  } as LoggerConfig,

  // UI Configuration
  ui: {
    animations: {
      duration: 200,
      delay: 100,
    },
    debounceDelay: 300,
    toastDuration: 5000,
    timestampUpdateInterval: 1000,
  } as UIConfig,

  // User Limits Configuration
  userLimits: {
    defaults: {
      optimizationsToday: 5,
      trialsToday: 250,
      concurrentStudies: 2,
    },
  } as UserLimitsConfig,

  // Metric Thresholds for Dashboard Color Coding
  // Data-driven defaults from production database analysis (2,326 trials, Dec 2025)
  // 3-tier system: green (top 25%), yellow (middle 50%), red (bottom 25%)
  metricThresholds: {
    // Sharpe ratio: Industry standard thresholds
    // < 1 = poor, 1-2 = adequate, > 2 = very good
    sharpe: {
      green: 2.0,    // Very good risk-adjusted returns
      yellow: 1.0,   // Adequate, below = poor
    },
    // Sortino ratio: P75 = 1.48, P25 = 0.50
    sortino: {
      green: 1.5,    // Top 25% - green
      yellow: 0.5,   // Middle 50% - yellow, below = red
    },
    // Win rate (as percentage): P75 = 57.6%, P25 = 44.4%
    winRate: {
      green: 58,     // Top 25% - green
      yellow: 44,    // Middle 50% - yellow, below = red
    },
    // Drawdown: LOWER is better (inverted logic)
    // P25 = 0.60%, P75 = 1.18% - note the inversion
    drawdown: {
      green: 0.6,    // Best 25% (lowest drawdown) - green
      yellow: 1.2,   // Middle 50% - yellow, above = red
    },
    // Trades: Based on correlation analysis with strategy success rates
    // Sweet spot is 25-40 trades (52% good, 16% elite)
    // 15+ trades = 16%+ chance of good strategy
    // 10-14 trades = only 2.7% chance of good strategy
    // <10 trades = insufficient statistical sample
    trades: {
      green: 15,     // Minimum for statistical relevance (16%+ success rate)
      yellow: 10,    // Marginal sample (2.7% success rate)
    },
    // Profit factor: P75 = 3.56, P25 = 1.86
    profitFactor: {
      green: 3.55,   // Top 25% - green
      yellow: 1.85,  // Middle 50% - yellow, below = red
    },
    // Objective score: P75 = 0.138, P25 = 0.033
    objectiveScore: {
      green: 0.138,  // Top 25% - green
      yellow: 0.033, // Middle 50% - yellow, below = red
    },
  } as MetricThresholdsConfig,

  // Optimization Configuration
  optimization: {
    defaultParameters: {
      lookback_window: {
        min: 1,
        max: 100,
        default: 10,
      },
      entry_threshold: {
        min: 0.0,
        max: 5.0,
        default: 1.5,
        step: 0.1,
      },
      stop_loss_percent: {
        min: 0.1,
        max: 10.0,
        default: 2.0,
        step: 0.1,
      },
      take_profit_percent: {
        min: 0.1,
        max: 20.0,
        default: 4.0,
        step: 0.1,
      },
    },
    trialLimits: {
      min: 10,
      max: 1000,
      default: 100,
    },
  } as OptimizationConfig,

  // External Services
  external: {
    optunaDashboardUrl: getEnvVar('VITE_OPTUNA_DASHBOARD_URL', ''),
  },

  // Error Handling Configuration
  errorHandling: {
    circuitBreaker: {
      default: {
        threshold: 5,
        timeout: 60000, // 1 minute
        resetTimeout: 30000, // 30 seconds
      },
      studies: {
        threshold: 3,
        timeout: 30000, // 30 seconds
        resetTimeout: 15000, // 15 seconds
      },
      health: {
        threshold: 10,
        timeout: 120000, // 2 minutes
        resetTimeout: 60000, // 1 minute
      },
    },
    retry: {
      maxAttempts: 3,
      initialDelay: 1000, // 1 second
      maxDelay: 10000, // 10 seconds
      backoffMultiplier: 2,
    },
    monitoring: {
      enabled: import.meta.env.DEV,
      errorWindowMs: 60000, // 1 minute
      maxErrorsPerType: 10,
    },
  } as ErrorHandlingConfig,

  // Feature Flags - Enable error monitoring in production for better debugging
  features: {
    debugMode: import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEBUG === "true",
    loggerPanel: import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEBUG === "true",
    reactQueryDevtools: false,
    errorMonitor: true, // Keep error monitoring always enabled for production debugging
  },
} as const

// Validation function to ensure required environment variables are set
export function validateConfig(): void {
  const requiredEnvVars = [] as string[]

  if (requiredEnvVars.length > 0) {
    const missing = requiredEnvVars.filter(key => !import.meta.env[key])
    if (missing.length > 0) {
      console.warn('Missing required environment variables:', missing)
    }
  }

  if (
    !import.meta.env.VITE_API_URL &&
    !import.meta.env.VITE_API_BASE_URL &&
    import.meta.env.DEV
  ) {
    console.warn(
      'VITE_API_URL and VITE_API_BASE_URL are not set; defaulting to local proxy (/api).',
    )
  }
}

// Helper function to get nested config values with type safety
export function getConfigValue<T>(path: string, defaultValue?: T): T {
  const keys = path.split('.')
  let result: unknown = config
  
  for (const key of keys) {
    if (result && typeof result === 'object' && key in result) {
      result = (result as Record<string, unknown>)[key]
    } else {
      return defaultValue as T
    }
  }
  
  return result as T
}

// Export individual config sections for convenience
export const apiConfig = config.api
export const queryConfig = config.query
export const fileUploadConfig = config.fileUpload
export const loggerConfig = config.logger
export const uiConfig = config.ui
export const userLimitsConfig = config.userLimits
export const metricThresholdsConfig = config.metricThresholds
export const optimizationConfig = config.optimization
export const errorHandlingConfig = config.errorHandling

// Initialize config validation
if (import.meta.env.DEV) {
  validateConfig()
}
